/**
 * Deduplication for CSP violation reports forwarded to Datadog.
 *
 * `forwardReports: "all"` in datadog-init makes the browser SDK forward every
 * `securitypolicyviolation` event as an error log. A single blocked ad pixel
 * or retried connection fires that event on every attempt, so one stale CSP
 * allowlist entry can emit thousands of identical events per user and inflate
 * the Datadog bill without adding information.
 *
 * This module keys each violation by directive + blocked origin — the same
 * granularity at which we'd fix it in `@/config/csp` — and records first-seen
 * timestamps in localStorage. A violation is reported at most ONCE per browser
 * for as long as its entry survives (localStorage persists indefinitely; the
 * entry only leaves via the size-cap eviction or the user clearing site data).
 * A violation never seen before always passes, so new regressions are
 * reported immediately by every browser that first encounters them.
 *
 * Storage failures fail open (report rather than drop): losing dedup costs
 * money, losing a violation costs visibility.
 *
 * Uses `window.localStorage` directly rather than useTenantLocalStorage:
 * CSP violations are a property of the browser/extension environment, not of
 * tenant data, and the Datadog beforeSend hooks run outside React where hooks
 * are unavailable.
 */
import { safeJsonParse } from "@/lib/utils"

const STORAGE_KEY = "rhythms.csp-violation-dedup.v1"
// Bounds localStorage growth if something churns unique origins (oldest evicted).
const MAX_TRACKED_VIOLATIONS = 100

// Matches the message minted by @datadog/browser-core's
// buildRawReportErrorFromCspViolation:
//   csp_violation: '<blockedURI>' blocked by '<effectiveDirective>' directive
// Capture groups: [1] blockedURI, [2] effectiveDirective.
const CSP_VIOLATION_MESSAGE_PATTERN = /'([^']*)' blocked by '([^']*)' directive/

type ViolationStore = Record<string, number>

/**
 * The Datadog sink evaluating the violation. The logs and RUM SDKs each see
 * the same securitypolicyviolation event through their own beforeSend, so the
 * dedup key is scoped per sink — otherwise whichever hook runs first would
 * swallow the other sink's first (and only) report.
 */
type CspViolationSink = "logs" | "rum"

/**
 * Collapse a blockedURI to its origin so query params and paths don't defeat
 * dedup. Non-URL values (`inline`, `eval`, `wasm-eval`, `data`, ...) and
 * non-hierarchical schemes whose origin serializes as "null" are kept as-is.
 */
function normalizeBlockedUri(blockedUri: string): string {
  try {
    const { origin } = new URL(blockedUri)
    return origin === "null" ? blockedUri : origin
  } catch {
    return blockedUri
  }
}

function readStore(): ViolationStore {
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return {}
  // A corrupt store parses to the {} fallback so the next write self-heals it.
  const parsed = safeJsonParse<unknown, Record<string, never>>(raw, {})
  if (typeof parsed !== "object" || parsed === null) return {}
  return Object.fromEntries(
    Object.entries(parsed).filter(
      (entry): entry is [string, number] => typeof entry[1] === "number"
    )
  )
}

// Enforce the size cap, evicting the oldest first-seen entries.
function prune(store: ViolationStore): ViolationStore {
  return Object.fromEntries(
    Object.entries(store)
      .toSorted(([, a], [, b]) => b - a)
      .slice(0, MAX_TRACKED_VIOLATIONS)
  )
}

/**
 * Whether `message` is a CSP violation this browser has already reported
 * (i.e. the caller should drop it). Non-CSP messages and first occurrences
 * return false; first occurrences are recorded so every later occurrence
 * dedupes for the lifetime of the browser's localStorage.
 */
export function isDuplicateCspViolation(
  sink: CspViolationSink,
  message?: string
): boolean {
  const match = message?.match(CSP_VIOLATION_MESSAGE_PATTERN)
  if (!match) return false
  const [, blockedUri = "", directive = ""] = match
  const key = `${sink}|${directive}|${normalizeBlockedUri(blockedUri)}`

  try {
    const store = readStore()
    if (store[key] !== undefined) return true
    store[key] = Date.now()
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prune(store)))
    return false
  } catch {
    return false
  }
}
